import React, { createContext, useContext, useState, useEffect } from 'react';
import { googleLogout } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import secureStorage, { migrateToSecureStorage } from '../utils/secureStorage';
import { sanitize } from '../utils/validation';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Initialize session from secure storage
    useEffect(() => {
        const initializeAuth = () => {
            try {
                // Migrate existing data on first load
                migrateToSecureStorage();
                
                const storedSession = secureStorage.getItem('auth_session');
                const storedUser = secureStorage.getItem('user_data');
                
                if (storedSession && storedUser) {
                    setSession(storedSession);
                    setUser(storedUser);
                    setLoading(false);
                } else {
                    setLoading(false);
                }
            } catch (e) {
                console.error('Error during auth initialization:', e);
                setLoading(false);
            }
        };
        
        initializeAuth();
    }, []);

    const login = async (credential) => {
        try {
            setError(null);
            setLoading(true);
            
            // Validate credential
            if (!credential || typeof credential !== 'string') {
                throw new Error('Invalid credential format');
            }
            
            console.log('🔑 Attempting login with credential:', credential.substring(0, 50) + '...');
            
            const decoded = jwtDecode(credential);
            console.log('🔓 Decoded token payload:', {
                email: decoded.email,
                name: decoded.name,
                exp: decoded.exp,
                iss: decoded.iss
            });
            
            // Sanitize decoded data
            const sanitizedEmail = sanitize.email(decoded.email || '');
            const sanitizedName = sanitize.text(decoded.name || '');
            
            console.log('📡 Sending request to backend:', `${process.env.REACT_APP_BACKEND_URL}/api/auth/google`);
            
            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/google`, {}, {
                headers: { 
                    'Authorization': `Bearer ${credential}`,
                    'Content-Type': 'application/json'
                },
                timeout: 15000 // Increased timeout
            });
            
            console.log('📥 Backend response:', response.status, response.data);

            if (response.data.success) {
                const userData = {
                    ...response.data.user,
                    email: sanitizedEmail,
                    name: sanitizedName,
                    token: credential
                };
                
                setUser(userData);
                setSession(credential);
                
                // Store in secure storage
                secureStorage.setItem('auth_session', credential);
                secureStorage.setItem('user_data', userData);
                
                console.log('✅ Login successful for user:', sanitizedName);
                setLoading(false);
                return { success: true };
            } else {
                throw new Error(response.data.error || 'Authentication failed');
            }
        } catch (error) {
            console.error('❌ Login failed:', error);
            console.error('❌ Error response:', error.response?.data);
            const errorMessage = error.response?.data?.error || error.message || 'Login failed. Please try again.';
            setError(errorMessage);
            setLoading(false);
            return { success: false, error: errorMessage };
        }
    };

    const logout = () => {
        try {
            googleLogout();
            setUser(null);
            setSession(null);
            setError(null);
            
            // Clear secure storage
            secureStorage.removeItem('auth_session');
            secureStorage.removeItem('user_data');
            
            // Clear any remaining localStorage items
            localStorage.removeItem('auth_session');
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    useEffect(() => {
        const initAuth = async () => {
            if (session) {
                try {
                    // Verify session/token with backend
                    await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/status`, {
                        headers: { 
                            'Authorization': `Bearer ${session}`,
                            'Content-Type': 'application/json'
                        },
                        timeout: 8000
                    });
                    
                    // Try to get stored user data first
                    let userData = secureStorage.getItem('user_data');
                    
                    if (!userData) {
                        // Fallback: decode the token
                        const decoded = jwtDecode(session);
                        userData = { 
                            email: sanitize.email(decoded.email || ''), 
                            name: sanitize.text(decoded.name || ''), 
                            token: session 
                        };
                    }
                    
                    setUser(userData);
                } catch (error) {
                    // Don't logout on verification failure - just use stored data
                    let userData = secureStorage.getItem('user_data');
                    if (userData) {
                        setUser(userData);
                    } else {
                        // Only logout if we have no stored data
                        logout();
                    }
                }
            }
            setLoading(false);
        };
        initAuth();
    }, [session]);

    return (
        <AuthContext.Provider value={{ 
            user, 
            session, 
            login, 
            logout, 
            isTableAdmin: user?.role === 'admin', 
            loading,
            error,
            clearError: () => setError(null)
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
